// ============================================================
// usePlatformIntegrations — Platform Control integrations hook.
//
// Backend-ready: when sq_integrations lands, the live branch will
// query it directly. Until then we return an honest empty list +
// loading=false so the surface renders an empty state instead of
// fake seeded providers. The catalog of *known* providers comes
// from a separate static list rendered alongside; this hook only
// reports what is actually configured for this workspace.
// ============================================================

import { useEffect, useState } from 'react'
import { getRuntime } from './_mode'
import {
  captureHookFailure,
  recordHookOutcome,
  withSpan,
} from '../observability/sentry'

export interface PlatformIntegration {
  id: string
  provider: string
  label: string
  status: 'connected' | 'error' | 'disabled'
  scope?: string
  updated_at?: string
}

export interface UsePlatformIntegrationsResult {
  integrations: PlatformIntegration[]
  loading: boolean
  error: string | null
  /** True when the backing table is missing — surfaces use this to
   *  show an "awaiting backend" state instead of "empty". */
  awaitingBackend: boolean
}

export function usePlatformIntegrations(): UsePlatformIntegrationsResult {
  const [integrations, setIntegrations] = useState<PlatformIntegration[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [awaitingBackend, setAwaitingBackend] = useState(false)

  useEffect(() => {
    let cancelled = false
    const { live, supabase } = getRuntime()

    async function load() {
      if (!live || !supabase) {
        if (cancelled) return
        setIntegrations([])
        setAwaitingBackend(true)
        setLoading(false)
        recordHookOutcome('usePlatformIntegrations', 'demo_fallback', { table: 'sq_integrations' })
        return
      }

      try {
        const { data, error: err } = await withSpan(
          'sq_integrations.select',
          'db.query',
          async () =>
            supabase
              .from('sq_integrations')
              .select('id,provider,label,status,scope,updated_at')
              .order('updated_at', { ascending: false }),
        )
        if (cancelled) return

        if (err) {
          const message = err.message || ''
          const missing =
            /relation .* does not exist/i.test(message) ||
            err.code === '42P01'
          setError(message)
          setIntegrations([])
          setAwaitingBackend(missing)
          if (!missing) {
            captureHookFailure('usePlatformIntegrations', 'live_error', err, {
              table: 'sq_integrations',
            })
          }
        } else {
          setIntegrations((data as PlatformIntegration[]) || [])
          setAwaitingBackend(false)
        }
      } catch (err) {
        if (cancelled) return
        setError((err as Error).message)
        setIntegrations([])
      }
      if (!cancelled) setLoading(false)
    }

    load()
    return () => {
      cancelled = true
    }
  }, [])

  return { integrations, loading, error, awaitingBackend }
}
